import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Menu,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import PanelEmptyState from '../../../Components/PanelEmptyState';
import { layout } from '../../../styles/theme';
import { getLotHistory } from '../../../services/lotHistoryService';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';
import { useLocatedLot } from '../../hooks/useLocatedLot';
import PanelShell from '../PanelShell/PanelShell';
import PanelHeader, { panelCloseIconButtonSx } from '../PanelShell/PanelHeader';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../ActionMenuItem';
import RecordLotIdentity, { recordCardHeaderIconSx } from '../RecordLotIdentity';
import RecordCountsLabel from '../RecordCountsLabel';
import ListSortMenu from '../ListSortMenu/ListSortMenu';
import { activityGroupLabelSx } from '../../../Leads/components/leadsUi.styles';
import {
  getRecordCardLocateActiveSx,
  listCardActionIconSx,
  listFiltersBoxSx,
  listSearchFieldSx,
  listSearchIconSx,
  recordCountsRowSx,
} from '../UserLotListsPanel/UserLotListsPanel.styles';
import {
  DEFAULT_HISTORY_SORT,
  HISTORY_SORT_OPTIONS,
  filterHistoryRows,
  formatHistoryCountsLabel,
  formatHistoryVisitedLabel,
  groupHistoryRows,
  normalizeHistoryRows,
} from './historyRows';
import {
  historyCardActionsSx,
  historyCardHeaderSx,
  historyCardSx,
  historyCardsStackSx,
  historyGroupsStackSx,
  historyHeaderZoneSx,
  historyPaperSx,
  historyScrollZoneSx,
  historySkeletonFieldSx,
  historySkeletonMutedSx,
  historySkeletonPrimarySx,
  historyToolbarBoxSx,
  historyToolbarZoneSx,
} from './HistoryPanel.styles';

const SIDEBAR_RAIL_WIDTH = layout.sidebarRailWidth;
const HISTORY_PANEL_DESCRIPTION = 'Lotes vistos recientemente.';
const HISTORY_LIMIT = 20;
const HISTORY_SEARCH_PLACEHOLDER = 'Ej: Nº catastral, Capital o Córdoba';
const GENERIC_LOAD_ERROR = 'No se pudo cargar el historial.';

/** Skeleton del buscador + fila de totales. */
const LoadingHistoryToolbar = () => (
  <Box sx={historyToolbarZoneSx} data-testid="history-toolbar-skeleton">
    <Box sx={{ ...listFiltersBoxSx, ...historyToolbarBoxSx }}>
      <Skeleton variant="rounded" height={44} sx={historySkeletonFieldSx} />
      <Box sx={recordCountsRowSx}>
        <Skeleton variant="rounded" width={112} height={14} sx={historySkeletonPrimarySx} />
        <Skeleton variant="rounded" width={148} height={14} sx={historySkeletonPrimarySx} />
      </Box>
    </Box>
  </Box>
);

/** Skeleton de la lista: rótulo de día + cards con la estructura de `RecordLotIdentity`. */
const LoadingHistoryCards = ({ groups = [3, 4] }) => (
  <Box sx={historyGroupsStackSx} data-testid="history-cards-skeleton">
    {groups.map((count, groupIdx) => (
      <Box key={groupIdx}>
        <Skeleton variant="rounded" width={groupIdx === 0 ? 32 : 72} height={11} sx={{ ...historySkeletonMutedSx, mb: 0.75 }} />
        <Box sx={historyCardsStackSx}>
          {Array.from({ length: count }).map((_, idx) => (
            <Box key={idx} sx={{ ...historyCardSx, cursor: 'default', '&:hover': {} }}>
              <Box sx={historyCardHeaderSx}>
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Skeleton variant="rounded" width={`${62 - (idx % 3) * 6}%`} height={18} sx={historySkeletonPrimarySx} />
                  <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                    <Skeleton variant="rounded" width={`${44 - (idx % 3) * 5}%`} height={14} sx={historySkeletonMutedSx} />
                  </Box>
                  <Skeleton variant="rounded" width={idx % 2 === 0 ? 108 : 132} height={12} sx={historySkeletonMutedSx} />
                </Box>
                <Box sx={historyCardActionsSx}>
                  <Skeleton variant="circular" width={18} height={18} sx={historySkeletonPrimarySx} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

/** Card de un lote visto: misma identidad que Registros; ⋮ solo con Ubicar. */
function HistoryLotCard({ row, groupLabel, locateActive, onOpenLot, onLocateLot }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const closeMenu = () => setMenuAnchor(null);
  return (
    <Box
      component="li"
      onClick={() => onOpenLot?.(row.lot_id)}
      data-testid="history-lot-card"
      data-located={locateActive ? 'true' : undefined}
      sx={{ ...historyCardSx, ...(locateActive ? getRecordCardLocateActiveSx() : {}) }}
    >
      <Box sx={historyCardHeaderSx}>
        <RecordLotIdentity lot={row} metaLine={formatHistoryVisitedLabel(row.visited_at, groupLabel)} />
        <Box onClick={(e) => e.stopPropagation()} sx={historyCardActionsSx}>
          <Tooltip title="Más acciones" placement="top" arrow>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ ...listCardActionIconSx, ...recordCardHeaderIconSx }}
              aria-label="Más acciones"
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={ACTION_MENU_PAPER_PROPS}
            MenuListProps={ACTION_MENU_LIST_PROPS}
          >
            {/* «Ver lote» no va: es lo mismo que tocar la card. */}
            <ActionMenuItem
              onClick={() => {
                closeMenu();
                onLocateLot?.(row.lot_id, row);
              }}
              icon={<MyLocationOutlinedIcon sx={{ fontSize: 20 }} />}
              label={locateActive ? 'Quitar marca del mapa' : 'Ubicar en el mapa'}
            />
          </Menu>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Historial: lotes que abrió el usuario (una card por lote, la última visita),
 * agrupados por día. Personal: en espacio ajeno no se monta (MapPage).
 * Ver `docs/maps/historial.md`.
 */
const HistoryPanel = ({
  uiLeftInset = SIDEBAR_RAIL_WIDTH,
  onClose,
  onOpenLot,
  onLocateLot,
  /** Ids a pintar en el mapa (el lote ubicado o `[]`): mismo resaltado que Registros. */
  onHighlightLotIdsChange,
}) => {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_HISTORY_SORT);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  // Ubicar centra el mapa; la card queda marcada y el lote pintado (abajo).
  const locateOnMap = useCallback((lotId) => onLocateLot?.(lotId), [onLocateLot]);
  const {
    locatedLotId,
    toggleLocatedLot,
    clearLocatedLot,
    isLotLocated,
  } = useLocatedLot({ onLocate: locateOnMap });

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const res = await getLotHistory({ limit: HISTORY_LIMIT, signal: controller.signal });
        if (controller.signal.aborted) return;
        if (res?._httpStatus === 401) {
          setHistory([]);
          setLoadError({ message: 'Iniciá sesión para ver tu historial.', retry: false });
        } else if (!res?._httpOk) {
          setHistory([]);
          setLoadError({ message: GENERIC_LOAD_ERROR, retry: true });
        } else {
          setHistory(normalizeHistoryRows(res?.data));
        }
        setLoading(false);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        setHistory([]);
        setLoadError({ message: GENERIC_LOAD_ERROR, retry: true });
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [reloadKey]);

  const filtered = useMemo(() => filterHistoryRows(history, search), [history, search]);
  const groups = useMemo(() => groupHistoryRows(filtered, new Date(), sortBy), [filtered, sortBy]);

  // El lote ubicado se pinta en el mapa (como Registros); al quitar la marca,
  // ubicar otro, ocultarlo con la búsqueda o cerrar el panel, se despinta.
  const onHighlightRef = useRef(onHighlightLotIdsChange);
  onHighlightRef.current = onHighlightLotIdsChange;
  useEffect(() => {
    onHighlightRef.current?.(locatedLotId ? [locatedLotId] : []);
  }, [locatedLotId]);
  useEffect(() => () => onHighlightRef.current?.([]), []);

  // Si la búsqueda saca la card ubicada, la marca no tiene dónde quedar.
  useEffect(() => {
    if (!locatedLotId) return;
    if (filtered.some((row) => row.lot_id === locatedLotId)) return;
    clearLocatedLot();
  }, [filtered, locatedLotId, clearLocatedLot]);

  const showToolbar = !loadError && (loading || history.length > 0);

  let content;
  if (loading) {
    content = <LoadingHistoryCards />;
  } else if (loadError) {
    content = (
      <PanelEmptyState
        testId="history-error-state"
        icon={ErrorOutlineIcon}
        title={loadError.message}
        hint={loadError.retry ? 'Revisá tu conexión y probá de nuevo.' : null}
        actionLabel={loadError.retry ? 'Reintentar' : null}
        actionIcon={RefreshIcon}
        onAction={loadError.retry ? () => setReloadKey((k) => k + 1) : null}
        sx={{ minHeight: 0, py: 0 }}
      />
    );
  } else if (history.length === 0) {
    content = (
      <PanelEmptyState
        testId="history-empty-state"
        icon={HistoryIcon}
        title="Todavía no viste ningún lote."
        hint="Los lotes que abras van a aparecer acá."
        sx={{ minHeight: 0, py: 0 }}
      />
    );
  } else if (filtered.length === 0) {
    content = (
      <PanelEmptyState
        testId="history-no-results"
        title="No hay resultados que coincidan"
        hint="Probá con otro número catastral o ubicación."
        sx={{ minHeight: 0, py: 0 }}
      />
    );
  } else {
    content = (
      <Box sx={historyGroupsStackSx}>
        {groups.map((group) => (
          <Box key={group.label} component="section" aria-label={group.label}>
            <Typography component="h3" sx={activityGroupLabelSx}>{group.label}</Typography>
            <Box component="ul" sx={{ ...historyCardsStackSx, listStyle: 'none', m: 0, p: 0 }}>
              {group.entries.map((row) => (
                <HistoryLotCard
                  key={row.lot_id}
                  row={row}
                  groupLabel={group.label}
                  locateActive={isLotLocated(row.lot_id)}
                  onOpenLot={onOpenLot}
                  onLocateLot={toggleLocatedLot}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <PanelShell uiLeftInset={uiLeftInset} onClose={onClose}>
      {({ snap }) => (
        <Paper elevation={0} sx={historyPaperSx}>
          <Box sx={historyHeaderZoneSx}>
            <PanelHeader
              title="HISTORIAL"
              subtitle={HISTORY_PANEL_DESCRIPTION}
              actions={(
                <IconButton onClick={onClose} size="small" aria-label="Cerrar panel" sx={panelCloseIconButtonSx}>
                  <CloseIcon />
                </IconButton>
              )}
            />
          </Box>

          {(snap === 'expanded' || snap === undefined) && showToolbar && (
            loading ? (
              <LoadingHistoryToolbar />
            ) : (
              <Box sx={historyToolbarZoneSx}>
                <Box sx={{ ...listFiltersBoxSx, ...historyToolbarBoxSx }}>
                  <TextField
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={HISTORY_SEARCH_PLACEHOLDER}
                    fullWidth
                    size="small"
                    variant="outlined"
                    inputProps={{ maxLength: 80, 'aria-label': 'Buscar en el historial' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={listSearchIconSx} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ ...listSearchFieldSx, mt: 0 }}
                  />
                  <Box sx={recordCountsRowSx}>
                    <RecordCountsLabel>{formatHistoryCountsLabel(filtered.length, history.length)}</RecordCountsLabel>
                    <ListSortMenu
                      value={sortBy}
                      onChange={setSortBy}
                      options={HISTORY_SORT_OPTIONS}
                      registrosStyle
                    />
                  </Box>
                </Box>
              </Box>
            )
          )}

          <Box
            ref={scrollRef}
            data-testid="history-scroll"
            sx={{
              ...historyScrollZoneSx,
              pt: showToolbar ? 1.5 : 1,
              ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
            }}
          >
            {content}
          </Box>
        </Paper>
      )}
    </PanelShell>
  );
};

export default HistoryPanel;
